import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Header, Popup, Icon, Flag, Loader } from 'semantic-ui-react';
import { Segment, Button, Table } from 'components';
import styled from 'styled-components';
import cn from 'classnames';
import Decimal from 'decimal.js';
import { colors } from 'utils/colors';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { EfficientFrontier, BuildOptimize, Storage } from 'actions';
import LineChart from './Graph';
import AvgReturn from './AvgReturn';
import SaveModal from '../Analytics/Menu/SaveModal';
import Loading from '../../../components/Loading';

@withRouter
class OptimizedPortfolios extends Component {
  constructor(props) {
    super(props);
    this.state = {
      returnRate: '',
      avgReturnVals: {},
      disableRecalculate: true,
      saveModalOpen: false,
      currentPortfolioInfo: {},
      currentEF: {},
      isValidWeight: true,
      isValidInput: true,
    };
    this.handleUpdateAvgVals = this.handleUpdateAvgVals.bind(this);
  }

  componentWillMount = () => {
    const { dispatch, user_region, params } = this.props;
    dispatch(Storage.setItem('toggle_region', user_region));
    dispatch(Storage.removeItem('pwpa-check-history')); // Clear local storage for include all holdings toggle cache
    dispatch(Storage.removeItem('pwpa-has-exclude-holding'));

    return dispatch(EfficientFrontier.fetchParams(params));
  };

  componentDidUpdate(prevProps) {
    const { efficientfrontier, buildoptimize } = this.props;
    if (prevProps.efficientfrontier !== this.props.efficientfrontier) {
      if (efficientfrontier.frontier.length === 0) {
        const prev = { ...buildoptimize.prevEF };
        this.handleChange(prev, 'currentEF');
        this.handleChange(this.avgReturnVals(prev), 'avgReturnVals');
        return;
      }
      this.handleChange(efficientfrontier, 'currentEF');
      this.handleChange(this.avgReturnVals(efficientfrontier), 'avgReturnVals');
      this.handleChange(true, 'disableRecalculate');
    }
  }

  getMaxMinWeight = () => {
    const { currentEF } = this.state;
    if (currentEF.included_tickers && currentEF.included_tickers.length > 0) {
      return new Decimal(100)
        .dividedBy(currentEF.included_tickers.length)
        .toFixed(2, Decimal.ROUND_DOWN);
    }
    return 0;
  };

  round = (num) => Math.round(num * 100) / 100;

  handleChange = (value, type) => {
    this.setState({ [type]: value });
  };

  handleAbort = () => {
    const { dispatch } = this.props;
    return dispatch(EfficientFrontier.abortFetching());
  };

  handleUpdateMinWeight = (value) => {
    const { dispatch } = this.props;
    const floated = new Decimal(value || 0).dividedBy(100).toNumber();
    const maxMinWeight = new Decimal(this.getMaxMinWeight())
      .dividedBy(100)
      .toNumber();
    if (floated <= maxMinWeight) {
      this.setState({ disableRecalculate: false });
      this.setState({ isValidWeight: true });
    } else {
      this.setState({ disableRecalculate: true });
      this.setState({ isValidWeight: false });
    }
    return dispatch(BuildOptimize.update('minWeight', floated));
  };

  handleUpdateAvgVals = (value, ticker) => {
    const { avgReturnVals } = this.state;
    if (ticker === undefined) {
      return;
    }
    const floated = new Decimal(value || 0).dividedBy(100);
    const newReturnVals = { ...avgReturnVals };
    newReturnVals[ticker] = floated.toNumber();
    this.setState({ avgReturnVals: newReturnVals });
    this.setState({ disableRecalculate: false });
  };

  handleUpdateCurrentPortfolio = (returnRate) => {
    const { currentEF } = this.state;
    if (currentEF.frontier.length > 0) {
      if (returnRate !== '') {
        const result = currentEF.frontier.find((portfolio) => {
          const num = new Decimal(returnRate)
            .dividedBy(100)
            .toFixed(3, Decimal.ROUND_DOWN);
          if (String(portfolio.return).length < 5) {
            const newNum = (new Decimal(num) * 1).toString();
            return String(portfolio.return).startsWith(newNum);
          }
          return String(portfolio.return).startsWith(num);
        });
        this.setState({ currentPortfolioInfo: result });
      }
    }
  };

  filterIncludedHoldings = (currentPortfolioInfo, type) => {
    if (currentPortfolioInfo && Object.keys(currentPortfolioInfo).length > 0) {
      const currentPortfolio = currentPortfolioInfo.portfolio;
      if (type === 'included') {
        return Object.keys(this.props.selectedHoldings)
          .filter((holding) => holding in currentPortfolio)
          .reduce((obj, key) => {
            obj[key] = this.props.selectedHoldings[key];
            return obj;
          }, {});
      }
      const result = Object.keys(this.props.selectedHoldings)
        .filter((holding) => holding in currentPortfolio === false)
        .reduce((obj, key) => {
          obj[key] = this.props.selectedHoldings[key];
          return obj;
        }, {});
      return result;
    }
    return {};
  };

  labelData = () => {
    const { selectedHoldings } = this.props;
    const totalValue = Object.values(selectedHoldings).reduce(
      (total, value) => (total += Number(value.weight)),
      0
    );

    if (!totalValue || this.round(totalValue) === 100) {
      return false;
    }

    return {
      val: this.round(Math.abs(100 - totalValue)),
      msg: 100 - totalValue > 0 ? 'under' : 'over',
    };
  };

  graphData = () => {
    const { currentEF } = this.state;
    let data = {
      efficient: [],
      original: [],
      xAxis: '',
      yAxis: '',
    };
    if (currentEF.frontier && currentEF.frontier.length > 0) {
      const efficientPortfolios = currentEF.frontier.map((point) => {
        const pointDict = {
          x: new Decimal(point.risk).times(100).toNumber(),
          y: new Decimal(point.return).times(100).toNumber(),
        };
        return pointDict;
      });
      const originalHoldings = currentEF.included_tickers.map((included) => {
        const pointDict = {
          x: new Decimal(included.risk).times(100).toNumber(),
          y: new Decimal(included.return).times(100).toNumber(),
          ticker: included.ticker,
        };
        return pointDict;
      });
      data = {
        efficient: efficientPortfolios,
        original: originalHoldings,
        xAxis: currentEF.x_axis,
        yAxis: currentEF.y_axis,
      };
    }
    return data;
  };

  avgReturnVals = (EF) => {
    const vals = {};
    if (EF.included_tickers && EF.included_tickers.length > 0) {
      EF.included_tickers.forEach(
        (included) => (vals[included.ticker] = included.return)
      );
    }
    return vals;
  };

  isValidWeight = (maxMinWeight) => {
    const { buildoptimize } = this.props;
    const { minWeight } = buildoptimize;
    return minWeight <= maxMinWeight;
  };

  isValidInput = (value) => {
    const { currentEF } = this.state;
    if (currentEF.frontier && currentEF.frontier.length > 0) {
      const min = new Decimal(currentEF.frontier[0].return).toDecimalPlaces(
        3,
        Decimal.ROUND_DOWN
      );
      const lastIndex = currentEF.frontier.length - 1;
      const max = new Decimal(
        currentEF.frontier[lastIndex].return
      ).toDecimalPlaces(3, Decimal.ROUND_DOWN);
      if (value !== '') {
        const returnDecimal = new Decimal(value)
          .dividedBy(100)
          .toDecimalPlaces(3, Decimal.ROUND_DOWN);
        return [min <= returnDecimal && returnDecimal <= max, min, max];
      }
      return [true, min, max];
    }
    return [true, new Decimal(0), new Decimal(0)];
  };

  // eslint-disable-next-line consistent-return
  formatDataForSaving = (currentPortfolioInfo) => {
    if (currentPortfolioInfo && Object.keys(currentPortfolioInfo).length > 0) {
      const currentPortfolio = currentPortfolioInfo.portfolio;
      // ex. {VTI:{...}, ABC: {...}} -> 'VTI,ABC'
      const symbols = Object.keys(currentPortfolio)
        .reduce((prev, curr) => prev.concat(`${curr},`), '')
        .slice(0, -1);
      // ex. {VTI:{..., weight:3}, ABC: {..., weight:5}} -> '3,5'
      const weights = Object.keys(currentPortfolio)
        .map((ticker) => currentPortfolio[ticker])
        .reduce((prev, curr) => prev.concat(`${curr},`), '')
        .slice(0, -1);
      return {
        symbols,
        weights,
        is_linked: false, // will show in 'from builders' tab on the 'Revisit a Saved Portfolio page'
        portfolio_type: 'custom',
      };
    }
  };

  recalculate = (givenRets) => {
    const { params, dispatch, efficientfrontier, buildoptimize } = this.props;
    const newMinWeight = buildoptimize.minWeight;
    const fullParams = { ...params };
    fullParams.given_annual_rets = givenRets;
    fullParams.min_weight = newMinWeight;
    this.setState({ returnRate: '' });
    this.setState({ currentPortfolioInfo: {} });
    if (efficientfrontier.frontier.length > 0) {
      const prevEF = { ...efficientfrontier };
      dispatch(BuildOptimize.update('prevEF', prevEF));
    }
    return dispatch(EfficientFrontier.fetchParams(fullParams));
  };

  analyze = (currentPortfolioInfo) => {
    const currentPortfolio = currentPortfolioInfo.portfolio;
    const { history, user_region, buildoptimize, dispatch } = this.props;
    dispatch(Storage.removeItem('pwpa-has-exclude-holding'));
    const params = Object.values(buildoptimize.portfolio).reduce(
      (total, item) => {
        if (
          item.ticker === '~BTCUSDT' ||
          item.ticker === '~ETHUSDT' ||
          (item.unadjusted_closing_price && item.unadjusted_closing_price.CAD)
        ) {
          const weight = currentPortfolio[item.ticker] || 0;
          total.symbols.push(item.ticker);
          total.weights.push(weight);
        }
        console.log(total);

        return total;
      },
      { symbols: [], weights: [] }
    );

    if (!params.symbols.length) {
      return false;
    }
    dispatch(
      Storage.setItem('pwpa-portfolio', {
        portfolio_type: 'custom',
        region: user_region || 'CA',
        symbols: params.symbols.join(','),
        weights: params.weights.join(','),
      })
    );

    return history.push('/portfolio/unsaved/overview');
  };

  renderTable(securities, type, currentPortfolioInfo) {
    const currentPortfolio = currentPortfolioInfo.portfolio;
    const { avgReturnVals } = this.state;
    return (
      <Segment
        className="basic compact"
        style={{
          opacity: EfficientFrontier.isFetching ? 0.5 : 1,
          marginTop: 0,
        }}
      >
        <Table
          textAlign="center"
          style={{
            marginBottom: '0rem',
            borderColor: !!this.labelData() && colors.red,
          }}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={{ background: 'rgba(203,232,238,0.3)' }}>
                Asset
              </Table.HeaderCell>
              {type === 'excluded' && (
                <Table.HeaderCell
                  style={{ background: 'rgba(203,232,238,0.3)' }}
                >
                  Exchange
                </Table.HeaderCell>
              )}
              <Table.HeaderCell style={{ background: 'rgba(203,232,238,0.3)' }}>
                Class
              </Table.HeaderCell>
              <Table.HeaderCell style={{ background: 'rgba(203,232,238,0.3)' }}>
                Price
              </Table.HeaderCell>
              {type === 'excluded' && (
                <Table.HeaderCell
                  style={{ background: 'rgba(203,232,238,0.3)' }}
                >
                  Start Date
                  <Popup
                    trigger={
                      <Icon
                        name="question circle outline"
                        style={{
                          fontSize: '1rem',
                          verticalAlign: 'top',
                          marginLeft: '0.3rem',
                        }}
                      />
                    }
                    position="right center"
                    content="The start date is the month after the inception/IPO date of your holding or April 2007, whichever comes later."
                  />
                </Table.HeaderCell>
              )}
              {type !== 'excluded' && (
                <Table.HeaderCell
                  style={{ background: 'rgba(203,232,238,0.3)' }}
                >
                  Avg. Return
                  <Popup
                    trigger={
                      <Icon
                        name="question circle outline"
                        style={{
                          fontSize: '1rem',
                          verticalAlign: 'top',
                          marginLeft: '0.3rem',
                        }}
                      />
                    }
                    position="right center"
                    content="Use your forward-looking expected returns to generate a new frontier by changing the average returns in the table and clicking recalculate."
                  />
                </Table.HeaderCell>
              )}
              {type !== 'excluded' && (
                <Table.HeaderCell
                  style={{ background: 'rgba(203,232,238,0.3)' }}
                >
                  Weight
                </Table.HeaderCell>
              )}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {securities &&
              Object.keys(securities).length > 0 &&
              Object.values(securities).map((security, index) => (
                <StyledRow
                  negative={!security.unadjusted_closing_price.CAD}
                  key={security.ticker}
                  style={{ height: 52, position: 'relative' }}
                >
                  <Table.Cell width={5} style={{ position: 'relative' }}>
                    {security.ticker || 'N/A'}
                    <SecurityName>{security.long_name || 'N/A'}</SecurityName>
                    {!security.unadjusted_closing_price.CAD && (
                      <Error>
                        <Popup
                          trigger={
                            <Icon
                              style={{
                                transform: 'translate(-20px, -4px)',
                                position: 'absolute',
                                display: 'block',
                                marginTop: '0',
                              }}
                              name="exclamation circle"
                            />
                          }
                          position="left center"
                          content="Unfortunately, this security cannot be added as its price is $0"
                        />
                      </Error>
                    )}
                  </Table.Cell>
                  {type === 'excluded' && (
                    <Table.Cell>{security.exchange_symbol || 'N/A'}</Table.Cell>
                  )}
                  <Table.Cell>
                    {security.global_asset_class || 'N/A'}
                  </Table.Cell>
                  <Table.Cell>
                    $
                    {this.round(
                      security.unadjusted_closing_price[
                        security.default_currency
                      ] || 0
                    )}
                    <Flag
                      name={security.default_currency === 'CAD' ? 'ca' : 'us'}
                      style={{ marginLeft: '0.3rem' }}
                    />
                  </Table.Cell>
                  {type === 'excluded' && (
                    <Table.Cell style={{ width: '170px' }}>
                      {security.start_date || 'N/A'}
                    </Table.Cell>
                  )}
                  {type !== 'excluded' && (
                    <Table.Cell
                      style={{ color: colors.blue, cursor: 'pointer' }}
                      onClick={(e) =>
                        this.handleChange(e.target.value, 'selectedCell', index)
                      }
                    >
                      <AvgReturn
                        value={avgReturnVals[security.ticker]}
                        handler={this.handleUpdateAvgVals}
                        ticker={security.ticker}
                      />
                    </Table.Cell>
                  )}
                  {type !== 'excluded' && (
                    <Table.Cell
                      style={{
                        background: colors.lightGrey,
                        fontWeight: 'bold',
                      }}
                    >
                      {(currentPortfolio &&
                        `${(currentPortfolio[security.ticker] * 100).toFixed(
                          2
                        )}%`) ||
                        'N/A'}
                    </Table.Cell>
                  )}
                </StyledRow>
              ))}
          </Table.Body>
        </Table>
        <Segment style={{ margin: '0px', padding: '0px' }}>
          {!!this.labelData() && (
            <Label>{`${this.labelData().val}% ${this.labelData().msg}`}</Label>
          )}
        </Segment>
      </Segment>
    );
  }

  render() {
    const { isClientMode, handler, efficientfrontier, buildoptimize } =
      this.props;
    const { isFetching } = efficientfrontier;
    const {
      returnRate,
      avgReturnVals,
      disableRecalculate,
      currentPortfolioInfo,
      saveModalOpen,
      currentEF,
      isValidWeight,
      isValidInput,
    } = this.state;
    const minMaxPortfolios = this.isValidInput(returnRate);
    const { minWeight } = buildoptimize;
    const maxMinWeight = this.getMaxMinWeight();

    return (
      <div style={{ height: '100%' }}>
        <Segment
          style={{
            minHeight: '80vh',
            height: '100%',
            textAlign: 'center',
            padding: '2rem 6rem',
            justifyContent: 'center',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              className="left"
              color={colors.lightGrey}
              icon="chevron left"
              onClick={() => {
                handler([]);
                this.handleAbort();
              }}
            >
              Back
            </Button>
          </div>
          {isFetching ? (
            <Loader active>
              <Header>Fetching optimizations.</Header>
            </Loader>
          ) : (
            <div>
              <Header
                size="medium"
                textAlign="center"
                style={{ maxWidth: 'fitContent' }}
              >
                Efficient Frontier Optimizer
                <Header.Subheader
                  style={{
                    marginTop: '1rem',
                    width: '73ch',
                    textAlign: 'center',
                  }}
                >
                  The blue curve below is the Efficient Frontier, showing the
                  portfolios with the lowest risk at each level of return. Enter
                  a return below to see the portfolio composition with the
                  optimized weights. The estimation is based on historical
                  returns. You can use your forward-looking expected returns to
                  generate a new frontier by changing the average returns in the
                  table and recalculate.
                </Header.Subheader>
              </Header>
              <div
                style={{
                  padding: '2rem 0 2rem 0',
                  minWidth: '80ch',
                  width: '80%',
                  margin: '0 auto',
                }}
              >
                {!isFetching && (
                  <div style={{ fontSize: '1rem', color: 'rgba(0,0,0,.5)' }}>
                    The yellow dots represent the securities you selected. Hover
                    over a dot or a point on the frontier to see the
                    risk/return.
                  </div>
                )}
                {isFetching ? (
                  <Loading active />
                ) : (
                  <LineChart data={this.graphData()} />
                )}
              </div>
              {!isFetching && (
                <div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      padding: '0 1rem 0 1rem',
                      alignItems: 'end',
                      marginTop: '2rem',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        textAlign: 'left',
                        width: '100%',
                      }}
                    >
                      <Header
                        style={{ fontSize: '15.5px', marginBottom: '.3rem' }}
                        content="Optimal Portfolio Weights"
                      />
                      <Note>
                        Calculated based on your selected risk measure and the
                        average returns of each holding.
                      </Note>
                      {Object.keys(currentEF).length > 0 && (
                        <Note style={{ marginTop: '0.5rem' }}>
                          {currentEF.init_date.replaceAll('-', '/')} -{''}
                          {currentEF.end_date.replaceAll('-', '/')}
                        </Note>
                      )}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'start',
                            justifyContent: 'end',
                            padding: '1rem 0 0 0',
                            height: '100px',
                            rowGap: '0.5rem',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              justifyItems: 'end',
                              rowGap: '0.5rem',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                columnGap: '0.5rem',
                                alignItems: 'start',
                              }}
                            >
                              <Header
                                style={{
                                  fontSize: '15.5px',
                                  marginBottom: '0rem',
                                }}
                                content="Enter your target annual return to see the portfolio."
                              />
                            </div>
                            <Header.Subheader style={{ textAlign: 'start' }}>
                              Range:{' '}
                              {minMaxPortfolios[1]
                                .times(100)
                                .toFixed(1, Decimal.ROUND_DOWN)}
                              % -{' '}
                              {minMaxPortfolios[2]
                                .times(100)
                                .toFixed(1, Decimal.ROUND_DOWN)}
                              %
                            </Header.Subheader>
                          </div>
                          <div
                            className={`ui mini right icon input ${
                              !isValidInput && 'error'
                            }`}
                            style={{ width: '7rem' }}
                          >
                            <Input
                              type="number"
                              name="returnRate"
                              value={returnRate}
                              onChange={(e) => {
                                const targetValue = e.target.value;
                                let newValue;
                                if (targetValue) {
                                  newValue = new Decimal(targetValue)
                                    .toDecimalPlaces(1, Decimal.ROUND_DOWN)
                                    .toNumber();
                                } else {
                                  newValue = '';
                                }
                                this.handleChange(newValue, 'returnRate');
                                this.handleChange(
                                  this.isValidInput(newValue)[0],
                                  'isValidInput'
                                );
                                this.handleUpdateCurrentPortfolio(newValue);
                              }}
                            />
                            <i
                              className="percent icon"
                              style={{ color: colors.grey }}
                            />
                          </div>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'end',
                            justifyContent: 'end',
                            padding: '1rem 0 0 0',
                            height: '100px',
                            rowGap: '0.5rem',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              justifyItems: 'end',
                              rowGap: '0.5rem',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                columnGap: '0.5rem',
                                alignItems: 'start',
                              }}
                            >
                              <Header
                                style={{
                                  fontSize: '15.5px',
                                  marginBottom: '0rem',
                                }}
                                content="Minimum Holding Weight:"
                              />
                              <Popup
                                trigger={
                                  <Icon
                                    name="question circle outline"
                                    style={{
                                      fontSize: '15px',
                                      color: 'dimgrey',
                                    }}
                                  />
                                }
                                wide
                              >
                                <Popup.Content>
                                  <b>Set a Minimum Holding Weight (Optional)</b>
                                  <br />
                                  The default is 0% for all holdings. You can
                                  change it and recalculate.
                                  <br />
                                  <br />
                                  <b>Note:</b> an upper bound is applied to
                                  ensure the holding weights do not exceed 100%.
                                </Popup.Content>
                              </Popup>
                            </div>
                            <Header.Subheader style={{ textAlign: 'end' }}>
                              Up to: {maxMinWeight}%
                            </Header.Subheader>
                          </div>
                          <div
                            className={`ui mini right icon input ${
                              !isValidWeight && 'error'
                            }`}
                            style={{ width: '7rem' }}
                          >
                            <Input
                              type="number"
                              name="minWeight"
                              placeholder="0"
                              value={
                                minWeight
                                  ? new Decimal(minWeight)
                                      .times(100)
                                      .toDecimalPlaces(2, Decimal.ROUND_DOWN)
                                      .toNumber()
                                  : ''
                              }
                              onChange={(e) => {
                                const targetValue = e.target.value;
                                let newValue;
                                if (targetValue) {
                                  newValue = new Decimal(targetValue)
                                    .toDecimalPlaces(2, Decimal.ROUND_DOWN)
                                    .toNumber();
                                } else {
                                  newValue = '';
                                }
                                this.handleUpdateMinWeight(newValue);
                              }}
                            />
                            <i
                              className="percent icon"
                              style={{ color: colors.grey }}
                            />
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          width: '100%',
                          marginTop: '1rem',
                        }}
                      >
                        {currentPortfolioInfo &&
                        Object.keys(currentPortfolioInfo).length > 0 ? (
                          <Header.Subheader>
                            <br /> Showing weights for the{' '}
                            <span style={{ color: 'red' }}>
                              {returnRate
                                ? new Decimal(returnRate).toFixed(
                                    2,
                                    Decimal.ROUND_DOWN
                                  )
                                : ''}
                              %
                            </span>{' '}
                            Annualized Return and{' '}
                            <span style={{ color: 'red' }}>
                              {new Decimal(currentPortfolioInfo.risk)
                                .times(100)
                                .toFixed(2, Decimal.ROUND_DOWN)}
                              %
                            </span>{' '}
                            {currentEF.x_axis} portfolio.
                          </Header.Subheader>
                        ) : (
                          <Header.Subheader>
                            <br /> Enter return rate above to view portfolio.
                          </Header.Subheader>
                        )}
                        <Button
                          className={cn('left', {
                            disabled: disableRecalculate,
                          })}
                          color={colors.blue}
                          icon="calculator left"
                          onClick={() => this.recalculate(avgReturnVals)}
                        >
                          Recalculate
                        </Button>
                      </div>
                    </div>
                  </div>
                  {this.renderTable(
                    this.filterIncludedHoldings(
                      currentPortfolioInfo || {},
                      'included'
                    ),
                    'included',
                    currentPortfolioInfo || {}
                  )}
                  {Object.keys(
                    this.filterIncludedHoldings(
                      currentPortfolioInfo || {},
                      'excluded'
                    )
                  ).length > 0 && (
                    <div
                      style={{
                        textAlign: 'left',
                        marginTop: '2rem',
                        padding: '0 1rem 0 1rem',
                      }}
                    >
                      <Header
                        style={{ fontSize: '15.5px', marginBottom: '.3rem' }}
                        content="Excluded due to less than 3 years of history"
                      />
                      {this.renderTable(
                        this.filterIncludedHoldings(
                          currentPortfolioInfo || {},
                          'excluded'
                        ),
                        'excluded',
                        currentPortfolioInfo || {}
                      )}
                    </div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'Center',
                      columnGap: '2rem',
                    }}
                  >
                    <Button
                      className={cn('left', {
                        disabled: !(
                          currentPortfolioInfo &&
                          Object.keys(currentPortfolioInfo).length > 0
                        ),
                      })}
                      color={colors.darkBlue}
                      icon="line chart"
                      onClick={() => this.analyze(currentPortfolioInfo || {})}
                    >
                      Analyze
                    </Button>
                    <Button
                      className={cn('left', {
                        disabled: !(
                          currentPortfolioInfo &&
                          Object.keys(currentPortfolioInfo).length > 0
                        ),
                      })}
                      color={colors.red}
                      icon="save"
                      onClick={() => this.handleChange(true, 'saveModalOpen')}
                    >
                      Save
                    </Button>
                    <SaveModal
                      closeModal={() =>
                        this.handleChange(false, 'saveModalOpen')
                      }
                      open={saveModalOpen}
                      portfolio={this.formatDataForSaving(
                        currentPortfolioInfo || {}
                      )}
                      redirectToSavedPortfolioOnSuccess={false}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </Segment>
      </div>
    );
  }
}

OptimizedPortfolios.propTypes = {
  history: PropTypes.object.isRequired,
  isClientMode: PropTypes.bool,
  dispatch: PropTypes.func,
  handler: PropTypes.func,
  user_region: PropTypes.string,
  params: PropTypes.object,
  selectedHoldings: PropTypes.object,
  efficientfrontier: PropTypes.object,
  buildoptimize: PropTypes.object,
};

OptimizedPortfolios.defaultProps = {
  isClientMode: false,
  dispatch: () => false,
  handler: () => false,
  user_region: 'CA',
  params: '',
  selectedHoldings: {},
  efficientfrontier: {},
  buildoptimize: {},
};

export default connect((state) => ({
  efficientfrontier: state.EfficientFrontier,
  buildoptimize: state.BuildOptimize,
}))(OptimizedPortfolios);

const Input = styled.input`
  display: block;
  margin-left: 0rem;
  padding: 0.5rem;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  transition: all 200ms ease;
  color: rgba(0, 0, 0, 0.8);
  width: 100%;
  max-width: 73px;
  font-size: 0.9rem;
  outline: none;

  &:focus,
  &:active {
    outline: none;
    border: 1px solid rgb(132, 183, 217);
  }

  &.invalid {
    border: 1px solid ${colors.red};
    background: rgb(255, 240, 240);
  }
`;

const Note = styled.div`
  font-size: 1.1rem;
  font-style: italic;
  color: rgba(0, 0, 0, 0.5);
`;

const SecurityName = styled.div`
  font-size: 0.9rem;
  line-height: 1rem;
  color: ${(props) =>
    props.negative ? 'rgba(159,58,56, 0.6)' : 'rgba(0,0,0,0.5)'};
`;

const StyledRow = styled(({ className, children, ...rest }) => (
  <Table.Row className={className} {...rest}>
    {children}
  </Table.Row>
))`
  position: relative !important;

  .close {
    position: absolute;
    display: block;
    margin: -10px;
    margin-top: 20px;
    z-index: 100;
    transform: rotate(45deg);
    text-align: center;
    font-size: 1.7rem;
    font-style: normal;
    border-radius: 18px;
    height: 20px;
    width: 20px;
    line-height: 17px;
    padding: 0 1px;
    background: white;
    border: 1px solid #e4e5e5;
    color: black;
    cursor: pointer;
    transition: background 0.1s ease, border 0.1s ease;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;

    &:hover {
      background: #fff6f6;
      border: 1px solid #db2828;
    }
  }
`;

const Label = styled.div`
  display: inline-block;
  position: absolute;
  bottom: -10px;
  left: 50%;
  color: white;
  transform: translateX(-50%);
  padding: 1px 20px;
  font-size: 0.9rem;
  background: ${colors.red};
  text-align: center;
  border-radius: 12px;
`;

const Error = styled.div`
  font-size: 1.7rem;
  position: absolute;
  top: 50%;
  left: -2.3rem;
  margin: -0.8rem auto;
  color: ${colors.red};
`;
